import { useTranslation, withTranslation } from 'react-i18next';
import {
  Typography,
  Box,
  InputLabel,
  Input,
  FormControl,
  InputAdornment,
  Link,
} from '@material-ui/core';
import { useState } from 'react';
import compose from 'ramda/src/compose';
import LocaleKeys from '../../../../../locales/keys';
import { makeStyles } from '@material-ui/core/styles';
import showIcon from '../../../../../images/icon-show.svg';
import hideIcon from '../../../../../images/icon-hide.svg';
import fieldLengths from '../../../../../utils/fieldLengths';

const useStyles = makeStyles((theme) => ({
  'field__input--error': {
    borderColor: theme.palette.error.main,
    borderWidth: '2px',
  },
  password__requirement: {
    display: 'inline-block',
    marginRight: 26,
    fontFamily: 'TitilliumWeb-Regular',
    fontSize: '17px',
    letterSpacing: '0.2px',
    WebkitFontSmoothing: 'auto',
  },
  incorrect_password__requirement: {
    fontSize: '12px',
    fontFamily: 'Formula1-Regular',
    fontWeight: 500,
    display: 'inline-block',
    color: theme.palette.error.main,
    marginRight: 26,
    letterSpacing: '0.2px',
    WebkitFontSmoothing: 'auto',
  },
  incorrect_strength_password__requirement: {
    display: 'inline-block',
    color: theme.palette.error.main,
    marginRight: 26,
    fontFamily: 'TitilliumWeb-Regular',
    fontSize: '17px',
    letterSpacing: '0.2px',
    WebkitFontSmoothing: 'auto',
  },
  link: {
    borderStyle: 'none',
    marginRight: '12px',
  },
  passwordField: {
    '&.MuiInputBase-root': {
      '&.Mui-focused': {
        boxShadow: '0 0 10px #9ecaed',
      },
    },
    fontSize: '15px',
    color: theme.palette.custom?.lightBlack,
  },
}));

function PasswordField(props) {
  const {
    form = {},
    name,
    externalData,
    language,
    isRule,
    label,
    autoFocusRef,
    customClasses,
    placeholder,
  } = props;
  const { values = {} } = form;
  const { t } = useTranslation();
  const classes = useStyles();
  const bullet = '\u25CF\xa0';
  const [showPassword, setShowPassword] = useState(false);

  const errors = form?.errors?.[name];

  const hasError = (field) => errors?.indexOf(field) > -1;
  const hasAnyError = () => errors?.length > 0;

  const displayRuleElements = [];

  const ruleContainer =
    externalData?.externalValidationRules?.fields?.[0]?.['password'];
  const rules = ruleContainer?.rules || [];

  const hasRuleError = () => {
    let result = false;
    rules.forEach((rule) => {
      if (errors?.indexOf(rule.name) > -1) {
        result = true;
      }
    });
    return result;
  };

  rules.forEach((rule) => {
    const ruleDescription =
      rule.description[language] || rule.description['default'];
    displayRuleElements.push(
      <Typography
        key={rule.name}
        display="inline"
        data-testid="password-rule-description"
        className={
          hasError(rule.name)
            ? classes.incorrect_strength_password__requirement
            : classes.password__requirement
        }
        variant="body2"
        color="textSecondary"
        data-cy={rule.name}
        style={{ paddingBottom: '4px' }}
      >
        {bullet}
        {ruleDescription}
      </Typography>,
    );
  });

  const passwordsMustBeDifferentError = hasAnyError(name) && !hasRuleError();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box className={`field field--${name}`} mb={4}>
      <InputLabel>
        <Typography
          variant="body1"
          color="textPrimary"
          className={customClasses && customClasses.titilliumFont}
        >
          {label || t(LocaleKeys.registration.password)}
        </Typography>
      </InputLabel>
      <FormControl
        className={`field__form-control ${
          customClasses && customClasses.fieldHeight
        }`}
      >
        <Input
          data-cy="field__input"
          type={showPassword ? 'text' : 'password'}
          className={`field__input ${classes.passwordField} ${
            form?.errors?.[name] ? classes['field__input--error'] : ''
          }`}
          disableUnderline
          inputRef={(input) => {
            autoFocusRef && (autoFocusRef.current = input);
          }}
          placeholder={placeholder || '**********'}
          fullWidth
          name={name}
          onChange={form.handleChange}
          value={values[name] || ''}
          data-testid={`password-${name}`}
          endAdornment={
            <InputAdornment position="end">
              <Link
                className={classes.link}
                underline="none"
                data-testid="toggle-password-visibility"
                onClick={togglePasswordVisibility}
              >
                <img
                  src={showPassword ? showIcon : hideIcon}
                  alt={
                    showPassword
                      ? t(LocaleKeys.registration.hidePassword)
                      : t(LocaleKeys.registration.showPassword)
                  }
                />
              </Link>
            </InputAdornment>
          }
          inputProps={{ maxLength: fieldLengths.password }}
        />
      </FormControl>
      {!isRule ? (
        <Box ml={3} mt={3}>
          {passwordsMustBeDifferentError && (
            <Typography
              variant="body2"
              color="textSecondary"
              data-cy="password__requirement"
              data-testid="password-no-rule-requirement"
              className={
                !hasRuleError()
                  ? classes.incorrect_password__requirement
                  : classes.password__requirement
              }
            >
              {t(LocaleKeys.accountInfo.passwordsMustBeDifferentError)}
            </Typography>
          )}
          <Typography
            variant="body2"
            color="textSecondary"
            data-cy="password__requirement"
            data-testid="password-no-rule-requirement"
            className={
              hasRuleError()
                ? classes.incorrect_strength_password__requirement
                : classes.password__requirement
            }
            style={{ paddingBottom: '6px' }}
          >
            {t(LocaleKeys.registration.passwordRequirement)}
          </Typography>
          <br />
          {displayRuleElements}
        </Box>
      ) : form?.errors?.[name] ? (
        <Box ml={3} mt={3}>
          <Typography
            variant="body2"
            color="textSecondary"
            data-cy="password__requirement"
            data-testid="password-rule-requirement"
            className={
              hasAnyError()
                ? classes.incorrect_password__requirement
                : classes.password__requirement
            }
          >
            {form?.errors?.[name]}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

export default compose(withTranslation())(PasswordField);
